import { getMeOptions, type MeResponseDto } from '@labflow/api-client';
import { useQuery } from '@tanstack/react-query';

import { serverFeaturesEnabled } from '../../lib/server-features';

/**
 * 로그인 세션 — E14 §5
 *
 * 세션은 전역 상태 라이브러리가 아니라 **Query 캐시**에 둔다. 쿠키 세션이므로 클라이언트가
 * 토큰을 들고 있지 않고(E13 §11), 서버에 물어보는 것이 유일한 진실이다.
 *
 * `retry: false`가 중요하다 — 로그아웃 상태의 `401`은 실패가 아니라 **정상 응답**이다.
 * 재시도하면 첫 화면이 그만큼 늦게 그려진다.
 */
export interface Session {
  user: MeResponseDto | null;
  isAuthenticated: boolean;
  /** 아직 서버 응답을 받지 못했다. 로그인 여부를 단정하지 않는다 */
  isPending: boolean;
}

export function useSession(options?: { enabled?: boolean }): Session {
  const enabled = (options?.enabled ?? true) && serverFeaturesEnabled;
  const query = useQuery({
    ...getMeOptions(),
    // 프리뷰 빌드는 API를 부르지 않는다 (D70 · lib/server-features.ts)
    enabled,
    retry: false,
    // 계정 정보는 자주 바뀌지 않는다. 초대 수락·권한 변경은 그 동작이 직접 무효화한다 (E14 §4.2)
    staleTime: 60_000,
  });

  return {
    user: query.data ?? null,
    isAuthenticated: query.data !== undefined,
    // 조회하지 않는 빌드에서는 `isPending`이 계속 참이다 — 로딩으로 보이지 않게 끊는다
    isPending: enabled && query.isPending,
  };
}
